import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { login, signup } from '../utils/api';
import { useAuth } from '../context/AuthContext';

export default function LoginPage() {
  useEffect(() => {
    document.title = 'Thunderbolt — Sign In';
    return () => { document.title = 'Thunderbolt'; };
  }, []);

  const [tab, setTab]         = useState('login');
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const { loginUser }         = useAuth();
  const navigate              = useNavigate();
  const [loginForm, setLoginForm]   = useState({ identifier: '', password: '' });
  const [signupForm, setSignupForm] = useState({
    name: '', email: '', phone: '', password: '', country: 'US'
  });

  async function handleLogin(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await login(loginForm);
      loginUser(res.data.token, res.data.user);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally { setLoading(false); }
  }

  async function handleSignup(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await signup(signupForm);
      loginUser(res.data.token, res.data.user);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Signup failed');
    } finally { setLoading(false); }
  }

  return (
    <div style={s.page}>
      <div style={s.hero}>
        <div style={s.logoWrap}>
          <img src="/tbold-logo-cal-04.svg" alt="Thunderbolt" style={s.logoImg}/>
        </div>
        <p style={s.heroSub}>Spend now. Stack for later.</p>
        <div style={s.heroBadges}>
          <div style={s.badge}>Instant global transfers</div>
          <div style={s.badge}>Boost your Stack</div>
          <div style={s.badge}>Flat fee</div>
        </div>
      </div>

      <div style={s.sheet}>
        <div style={s.tabRow}>
          {['login','signup'].map(t => (
            <button key={t}
              style={{...s.tab, ...(tab===t ? s.tabActive : {})}}
              onClick={() => { setTab(t); setError(''); }}>
              {t === 'login' ? 'Log in' : 'Sign up'}
            </button>
          ))}
        </div>

        {error && <div style={s.error}>{error}</div>}

        {tab === 'login' ? (
          <form onSubmit={handleLogin}>
            <div style={s.field}>
              <label style={s.label}>Email or phone</label>
              <input style={s.input} type="text"
                placeholder="you@email.com or +1 555 000 0000"
                value={loginForm.identifier}
                onChange={e => setLoginForm({...loginForm, identifier: e.target.value})}
                required/>
            </div>
            <div style={s.field}>
              <label style={s.label}>Password</label>
              <input style={s.input} type="password"
                placeholder="••••••••"
                value={loginForm.password}
                onChange={e => setLoginForm({...loginForm, password: e.target.value})}
                required/>
            </div>
            <button style={s.btn} type="submit" disabled={loading}>
              {loading ? 'Logging in...' : 'Log in'}
            </button>
            <div style={{textAlign:'center', marginTop:8}}>
              <span style={{fontSize:13, color:'#007AFF', cursor:'pointer'}} onClick={() => navigate('/forgot-password')}>Forgot password?</span>
            </div>
          </form>
        ) : (
          <form onSubmit={handleSignup}>
            <div style={s.field}>
              <label style={s.label}>Full name</label>
              <input style={s.input} type="text"
                placeholder="Jane Smith"
                value={signupForm.name}
                onChange={e => setSignupForm({...signupForm, name: e.target.value})}
                required/>
            </div>
            <div style={s.field}>
              <label style={s.label}>Email address</label>
              <input style={s.input} type="email"
                placeholder="you@email.com"
                value={signupForm.email}
                onChange={e => setSignupForm({...signupForm, email: e.target.value})}/>
            </div>
            <div style={s.field}>
              <label style={s.label}>Phone number</label>
              <input style={s.input} type="tel"
                placeholder="+1 (555) 000-0000"
                value={signupForm.phone}
                onChange={e => setSignupForm({...signupForm, phone: e.target.value})}/>
            </div>
            <div style={s.field}>
              <label style={s.label}>Password</label>
              <input style={s.input} type="password"
                placeholder="At least 8 characters"
                value={signupForm.password}
                onChange={e => setSignupForm({...signupForm, password: e.target.value})}
                required/>
            </div>
            <div style={s.field}>
              <label style={s.label}>Country</label>
              <select style={s.input}
                value={signupForm.country}
                onChange={e => setSignupForm({...signupForm, country: e.target.value})}>
                <option value="US">United States</option>
                <option value="CA">Canada</option>
              </select>
            </div>
            <button style={s.btn} type="submit" disabled={loading}>
              {loading ? 'Creating account...' : 'Create account'}
            </button>
            <p style={s.terms}>
              By creating an account you agree to our{' '}
              <a href="/terms" style={s.termsLink}>Terms of Service</a>.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

const s = {
  page:     { minHeight:'100vh', background:'#F5F5F7' },
  hero:     { background:'#007AFF', padding:'64px 24px 36px', textAlign:'center' },
  logoWrap: { width:225, height:64, borderRadius:20, backdropFilter:'blur(10px)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' },
  logoImg:  { width:'100%', height:'100%', objectFit:'contain' },
  logoMark: { width:15, height:15, display:'inline-flex', marginRight:5, verticalAlign:'middle' },
  heroSub:  { color:'rgba(255,255,255,0.85)', fontSize:16, marginBottom:16 },
  heroBadges: { display:'flex', gap:8, justifyContent:'center', flexWrap:'wrap' },
  badge:    { background:'rgba(255,255,255,0.15)', color:'#fff', fontSize:12, fontWeight:500, padding:'6px 12px', borderRadius:20, display:'inline-flex', alignItems:'center' },
  sheet:    { background:'#fff', borderRadius:'24px 24px 0 0', padding:'28px 24px 48px', marginTop:-20, minHeight:'calc(100vh - 200px)' },
  tabRow:   { display:'flex', background:'#F5F5F7', borderRadius:12, padding:4, gap:4, marginBottom:24 },
  tab:      { flex:1, padding:'10px 0', textAlign:'center', fontSize:14, fontWeight:500, border:'none', borderRadius:8, background:'transparent', color:'#6E6E73', cursor:'pointer' },
  tabActive:{ background:'#fff', color:'#1D1D1F', boxShadow:'0 1px 4px rgba(0,0,0,0.1)' },
  error:    { background:'#FFF0EF', border:'1px solid #FFD0CE', borderRadius:10, padding:'10px 14px', fontSize:13, color:'#FF3B30', marginBottom:16 },
  field:    { marginBottom:16 },
  label:    { display:'block', fontSize:13, fontWeight:500, color:'#1D1D1F', marginBottom:6 },
  input:    { width:'100%', padding:'13px 14px', border:'1.5px solid #F0F0F0', borderRadius:12, fontSize:15, background:'#FAFAFA', color:'#1D1D1F', outline:'none', boxSizing:'border-box' },
  btn:      { width:'100%', padding:16, background:'#007AFF', color:'#fff', border:'none', borderRadius:14, fontSize:16, fontWeight:600, letterSpacing:-0.3, marginTop:8, marginBottom:12, cursor:'pointer' },
  termsLink:{ color:'#007AFF', textDecoration:'none', fontWeight:500 },
  terms:    { fontSize:12, color:'#AEAEB2', textAlign:'center', lineHeight:1.5 },
};
